import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, linkedSignal, output, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FavoriteViewsService } from '@iot-platform/data-access/favorite-views';
import { CronConfiguratorComponent, CronPreview, computeNextRun, describeCron } from '@iot-platform/iot-platform-ui';
import { BusinessProfile, FavoriteView, FavoriteViewExport, FavoriteViewReportStatus } from '@iot-platform/models/common';
import { DateFormatPipe } from '@iot-platform/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, of, switchMap } from 'rxjs';

@Component({
  selector: 'iot-platform-ui-favorite-view-report-form',
  imports: [TranslatePipe, MatSlideToggleModule, MatIconModule, MatTooltipModule, DateFormatPipe, CronConfiguratorComponent],
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteViewReportFormComponent {
  private readonly favoriteViewsService: FavoriteViewsService = inject(FavoriteViewsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  favoriteView = input<FavoriteView>();
  businessProfileId = input<string>();
  formValue = output<FavoriteViewExport>();
  formStateChanged = output<boolean>();

  businessProfile: Signal<BusinessProfile | null | undefined> = toSignal(
    toObservable(this.businessProfileId).pipe(
      distinctUntilChanged(),
      switchMap((businessProfileId) => (businessProfileId ? this.favoriteViewsService.getBusinessProfileById(businessProfileId) : of(null))),
      takeUntilDestroyed(this.destroyRef)
    )
  );

  status: WritableSignal<FavoriteViewReportStatus> = linkedSignal<FavoriteView | undefined, FavoriteViewReportStatus>({
    source: this.favoriteView,
    computation: (favoriteView) => favoriteView?.export?.status ?? FavoriteViewReportStatus.DISABLED
  });

  cron: WritableSignal<string | null> = linkedSignal<FavoriteView | undefined, string | null>({
    source: this.favoriteView,
    computation: (favoriteView) => favoriteView?.export?.cron ?? null
  });

  timezone: Signal<string> = computed(() => this.businessProfile()?.timezoneDetails?.name ?? this.businessProfile()?.timezone ?? 'UTC');
  preview: Signal<CronPreview> = computed(() => describeCron(this.cron(), this.timezone()));
  nextRun: Signal<string | null> = computed(() => computeNextRun(this.cron(), this.timezone()));
  STATUS = FavoriteViewReportStatus;

  emitEffect = effect(() => {
    const status = this.status();
    const cron = this.cron();
    const currentExport = this.favoriteView()?.export;
    const changed = status !== currentExport?.status || cron !== currentExport?.cron;

    this.formStateChanged.emit(changed);
    this.formValue.emit({
      ...currentExport,
      status,
      cron
    });
  });
}
